import type { SupabaseClient } from '@supabase/supabase-js';
import { ensureOttoStyles, OTTO_ICON, ottoEscapeHtml } from './otto-ui.js';
import { httpErrorText } from '../lib/session-expiry.js';

interface Ctx { contentEl: HTMLElement; supabase: SupabaseClient; session: { token: string; tenantId: string; marketplaceKind?: string }; supabaseUrl: string; }
interface StatusRow { connected: boolean; status: string | null; installation_id: string | null; }

/**
 * Tab „API-Zugangsdaten" (/#/credentials). OTTO = Partner-App: der Händler verbindet sein
 * OTTO-Konto per OAuth-Consent (otto-oauth-start → neuer Tab → Callback otto-install), statt
 * Client-ID/Secret zu tippen. Diese View zeigt Status + Connect/Disconnect. KEINE Browser-Dialoge.
 */
export async function renderOttoCredentials(ctx: Ctx) {
  ensureOttoStyles();
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = `
    <div class="otto-install">
      <div class="stage"><div class="card">
        <div class="card-top">
          <img class="otto-badge" src="/otto-market.png" alt="OTTO Market">
          <div class="titles"><h2>OTTO-Konto</h2><div class="sub">Verbindung zu OTTO Market</div></div>
          <span class="env-tag" id="c-tag"><span class="d"></span>Getrennt</span>
        </div>
        <div class="card-body">
          <p class="intro">Verbinde dein <b>OTTO-Market-Konto</b> mit Vario. Du wirst zu OTTO weitergeleitet, bestätigst dort den Zugriff und kehrst danach hierher zurück. <b>Es sind keine API-Zugangsdaten einzutippen.</b></p>
          <div class="sec-lab">Status</div>
          <div id="c-state"><span class="sc-loading">${OTTO_ICON.info}Status wird geladen …</span></div>
        </div>
        <div class="card-foot">
          <span class="foot-status" id="c-foot">${OTTO_ICON.info}Noch nicht verbunden</span>
          <div class="foot-actions">
            <button class="btn btn-ghost" id="c-disconnect" hidden>${OTTO_ICON.trash}Verbindung trennen</button>
            <button class="btn btn-primary" id="c-connect" hidden>${OTTO_ICON.check}Mit OTTO verbinden</button>
          </div>
        </div>
      </div></div>
      <style>.otto-install .sc-loading{display:inline-flex;align-items:center;gap:8px;font-size:12.5px;color:var(--muted);}
        .otto-install #c-disconnect.armed{color:var(--danger);border-color:var(--danger);}</style>
    </div>`;

  const stateEl = ctx.contentEl.querySelector<HTMLDivElement>('#c-state')!;
  const footEl = ctx.contentEl.querySelector<HTMLSpanElement>('#c-foot')!;
  const tagEl = ctx.contentEl.querySelector<HTMLSpanElement>('#c-tag')!;
  const connectBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#c-connect')!;
  const disconnectBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#c-disconnect')!;
  const base = ctx.supabaseUrl.replace(/\/$/, '');
  let pollTimer: ReturnType<typeof setInterval> | undefined;
  let armed = false, armTimer: ReturnType<typeof setTimeout> | undefined;

  const setFoot = (k: 'info'|'ready'|'error', h: string) => { footEl.className = k==='info'?'foot-status':`foot-status ${k}`; footEl.innerHTML = h; };

  function renderConnected(row: StatusRow) {
    tagEl.className = 'env-tag prod'; tagEl.innerHTML = `<span class="d"></span>Verbunden`;
    stateEl.innerHTML = `<div class="sc-row" style="display:flex;gap:14px;align-items:center;border:1px solid #ECECEF;border-radius:10px;padding:13px 15px;">
      <div style="flex:1"><div style="font-size:13.5px;font-weight:600">OTTO Market verbunden</div>
      <div style="font-size:11.5px;color:var(--muted);font-family:var(--mono,monospace)">Installation: ${ottoEscapeHtml(row.installation_id ?? '—')}</div></div>
      <span style="font-size:10.5px;font-weight:600;color:#2E7D32;background:#E8F5E9;border-radius:999px;padding:3px 9px">aktiv</span></div>`;
    connectBtn.hidden = true; disconnectBtn.hidden = false; setFoot('ready', `${OTTO_ICON.check}OTTO-Konto verbunden`);
  }
  function renderDisconnected() {
    tagEl.className = 'env-tag'; tagEl.innerHTML = `<span class="d"></span>Getrennt`;
    stateEl.innerHTML = `<span class="sc-loading">${OTTO_ICON.info}Noch kein OTTO-Konto verbunden.</span>`;
    connectBtn.hidden = false; disconnectBtn.hidden = true; setFoot('info', `${OTTO_ICON.info}Noch nicht verbunden`);
  }

  async function loadStatus(): Promise<boolean> {
    const { data, error } = await ctx.supabase.rpc('otto_connection_status');
    if (error) { setFoot('error', `${OTTO_ICON.info}${ottoEscapeHtml(error.message)}`); return false; }
    const row = (Array.isArray(data) ? data[0] : data) as StatusRow | undefined;
    if (row?.connected) { renderConnected(row); return true; }
    renderDisconnected(); return false;
  }

  connectBtn.addEventListener('click', async () => {
    connectBtn.disabled = true; connectBtn.innerHTML = `<span class="spin"></span>Öffne OTTO …`;
    try {
      const res = await fetch(`${base}/functions/v1/otto-oauth-start`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` }, body: '{}',
      });
      const p = await res.json().catch(() => ({})) as { ok?: boolean; authUrl?: string; error?: { message: string } };
      connectBtn.disabled = false; connectBtn.innerHTML = `${OTTO_ICON.check}Mit OTTO verbinden`;
      if (!res.ok || !p.ok || !p.authUrl) { setFoot('error', `${OTTO_ICON.info}${ottoEscapeHtml(p.error?.message ?? httpErrorText(res.status))}`); return; }
      window.open(p.authUrl, '_blank', 'noopener');
      setFoot('info', `${OTTO_ICON.info}Bestätige den Zugriff im OTTO-Fenster …`);
      if (pollTimer) clearInterval(pollTimer);
      let ticks = 0;
      // Leise pollen: bei Erfolg auf „verbunden" flippen, sonst die „Bestätige …"-Meldung
      // stehen lassen (NICHT renderDisconnected pro Tick → kein Flackern). Nach ~3 Min Timeout.
      pollTimer = setInterval(async () => {
        ticks++;
        const { data } = await ctx.supabase.rpc('otto_connection_status');
        const row = (Array.isArray(data) ? data[0] : data) as StatusRow | undefined;
        if (row?.connected) { if (pollTimer) clearInterval(pollTimer); renderConnected(row); return; }
        if (ticks > 60) {
          if (pollTimer) clearInterval(pollTimer);
          setFoot('info', `${OTTO_ICON.info}Noch keine Bestätigung von OTTO — bei Bedarf erneut „Mit OTTO verbinden".`);
        }
      }, 3000);
    } catch (e) {
      connectBtn.disabled = false; connectBtn.innerHTML = `${OTTO_ICON.check}Mit OTTO verbinden`;
      setFoot('error', `${OTTO_ICON.info}Netzwerkfehler: ${ottoEscapeHtml((e as Error).message)}`);
    }
  });

  disconnectBtn.addEventListener('click', async () => {
    if (!armed) { armed = true; disconnectBtn.classList.add('armed'); disconnectBtn.innerHTML = `${OTTO_ICON.trash}Wirklich trennen?`; armTimer = setTimeout(() => { armed = false; disconnectBtn.classList.remove('armed'); disconnectBtn.innerHTML = `${OTTO_ICON.trash}Verbindung trennen`; }, 3000); return; }
    if (armTimer) clearTimeout(armTimer); armed = false; disconnectBtn.classList.remove('armed'); disconnectBtn.disabled = true;
    const { error } = await ctx.supabase.rpc('otto_disconnect');
    disconnectBtn.disabled = false; disconnectBtn.innerHTML = `${OTTO_ICON.trash}Verbindung trennen`;
    if (error) { setFoot('error', `${OTTO_ICON.info}${ottoEscapeHtml(error.message)}`); return; }
    renderDisconnected();
  });

  await loadStatus();
}
